import { AnyObject } from '@shared/types';

/**
 * This helper is a temporary solution until Angular team implement a stream to listen .dirty / .touched changes
 * We have to use ngDoCheck and call .markForCheck() manually if one of the attributes have been changed.
 *
 * @example
 *
 * ```typescript
 * @Component({ ..., changeDetection: ChangeDetectionStrategy.OnPush })
 * class MyComponent implements DoCheck {
 *   @Input()
 *   public control = new FormControl();
 *
 *   private dirtyOrTouchedChangeNotifier = new ControlDirtyAndTouchChangeNotifier(this);
 *
 *   public constructor(
 *     public readonly changeDetectorRef: ChangeDetectorRef,
 *   ) {}
 *
 *   public ngDoCheck(): void {
 *     this.dirtyOrTouchedChangeNotifier.check();
 *   }
 * }
 * ```
 *
 * TODO: 1. Use ɵmarkDirty instead of taking ChangeDetectorRef via constructor
 * TODO: 2. Add a version based on RxJS streams
 */
export class ControlDirtyAndTouchChangeNotifier<TTarget extends AnyObject> {
  private lastDirty: boolean = false;
  private lastTouched: boolean = false;

  public constructor(
    private readonly target: TTarget,
    private readonly controlFieldName: keyof typeof target = 'control',
    private readonly changeDetectorRefFieldName: keyof typeof target = 'changeDetectorRef',
  ) {
    this.lastDirty = this.getIsDirtyNow();
    this.lastTouched = this.getIsTouchedNow();
  }

  public check(): void {
    const newDirty = this.getIsDirtyNow();
    const newTouched = this.getIsTouchedNow();
    if (this.lastDirty !== newDirty || this.lastTouched !== newTouched) {
      this.target[this.changeDetectorRefFieldName].markForCheck();
    }
    this.lastDirty = newDirty;
    this.lastTouched = newTouched;
  }

  private getIsDirtyNow(): boolean {
    return this.target[this.controlFieldName]?.dirty ?? false;
  }
  private getIsTouchedNow(): boolean {
    return this.target[this.controlFieldName]?.touched ?? false;
  }
}
